import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import type { FaceAnalysis } from '../types';
import { analyzeFaceLandmarks } from '../lib/faceGeometry';
import {
  PHOTO_ACCEPT_ATTRIBUTE,
  canvasToPhotoFile,
  captureVideoFrame,
  describeUnknownError,
  preparePhoto,
  releasePreparedPhoto,
  stopMediaStream,
  type PreparedPhoto,
} from '../lib/imageUtils';
import { detectFaces, getVisionDelegate, warmFaceLandmarker } from '../lib/vision';
import {
  AlertIcon,
  CameraIcon,
  EyeIcon,
  EyeOffIcon,
  LockIcon,
  PasteIcon,
  RulerIcon,
  SparkIcon,
  UploadIcon,
} from './Icons';
import { ShapeGlyph } from './ShapeGlyph';

type PhotoStatus =
  | 'idle'
  | 'preparing'
  | 'loading-engine'
  | 'analyzing'
  | 'camera'
  | 'complete'
  | 'error';

interface PhotoAnalyzerProps {
  photo: PreparedPhoto | null;
  analysis: FaceAnalysis | null;
  onPhotoChange: (photo: PreparedPhoto | null) => void;
  onAnalysisChange: (analysis: FaceAnalysis | null) => void;
  onUseManual: () => void;
}

function analysisErrorFor(error: unknown): string {
  const message = describeUnknownError(error, 'The photo could not be analyzed.');
  const normalized = message.toLowerCase();

  if (normalized.includes('fetch') || normalized.includes('wasm') || normalized.includes('model')) {
    return 'The private analysis engine could not load. Check your connection once, then retry; the model is downloaded from this site and runs in your browser.';
  }
  return message;
}

function statusCopy(status: PhotoStatus): string {
  switch (status) {
    case 'preparing':
      return 'Preparing the photo in this browser…';
    case 'loading-engine':
      return 'Loading the private face-landmark engine…';
    case 'analyzing':
      return 'Mapping the visible face outline and comparing proportions…';
    case 'complete':
      return 'Analysis complete. Review the overlay and result below.';
    case 'camera':
      return 'Camera preview is open. Center one face and keep your head level.';
    case 'error':
      return 'Analysis needs another photo or the manual fallback.';
    case 'idle':
      return 'Ready for a portrait.';
  }
}

export function PhotoAnalyzer({
  photo,
  analysis,
  onPhotoChange,
  onAnalysisChange,
  onUseManual,
}: PhotoAnalyzerProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const runIdRef = useRef(0);
  const mountedRef = useRef(true);
  const [status, setStatus] = useState<PhotoStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [delegate, setDelegate] = useState<'GPU' | 'CPU' | null>(null);

  const ovalPath = useMemo(() => {
    if (!analysis || analysis.oval.length < 3) return '';
    return `${analysis.oval
      .map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(5)} ${point.y.toFixed(5)}`)
      .join(' ')} Z`;
  }, [analysis]);

  const closeCamera = useCallback(() => {
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
  }, []);

  const processFile = useCallback(
    async (file: File) => {
      const runId = runIdRef.current + 1;
      runIdRef.current = runId;
      setError(null);
      setShowOverlay(true);
      setDelegate(null);
      onAnalysisChange(null);
      onPhotoChange(null);
      setStatus('preparing');

      let prepared: PreparedPhoto | null = null;
      try {
        prepared = await preparePhoto(file);
        if (!mountedRef.current || runIdRef.current !== runId) {
          releasePreparedPhoto(prepared);
          return;
        }
        onPhotoChange(prepared);

        setStatus('loading-engine');
        await warmFaceLandmarker();
        if (!mountedRef.current || runIdRef.current !== runId) return;

        setStatus('analyzing');
        const faces = await detectFaces(prepared.analysisCanvas);
        if (!mountedRef.current || runIdRef.current !== runId) return;

        if (faces.length === 0) {
          throw new Error(
            'No complete face was detected. Use a straight-on, well-lit portrait with the forehead, cheeks, jaw, and chin visible.',
          );
        }
        if (faces.length > 1) {
          throw new Error(
            'More than one face was detected. Crop the photo so it contains one face, then analyze it again.',
          );
        }

        const landmarks = faces[0];
        if (!landmarks) throw new Error('The detected face mesh was empty. Try another portrait.');
        const nextAnalysis = analyzeFaceLandmarks(landmarks);
        onAnalysisChange(nextAnalysis);
        setDelegate(getVisionDelegate());
        setStatus('complete');
      } catch (caughtError) {
        if (!mountedRef.current || runIdRef.current !== runId) {
          if (prepared) releasePreparedPhoto(prepared);
          return;
        }
        setError(analysisErrorFor(caughtError));
        setStatus('error');
      }
    },
    [onAnalysisChange, onPhotoChange],
  );

  const openCamera = useCallback(async () => {
    runIdRef.current += 1;
    setError(null);
    onAnalysisChange(null);
    onPhotoChange(null);
    closeCamera();

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This browser does not expose a camera API. Upload a portrait or use manual measurements.');
      setStatus('error');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 1280 },
        },
      });
      if (!mountedRef.current) {
        stopMediaStream(stream);
        return;
      }
      streamRef.current = stream;
      setCameraOpen(true);
      setStatus('camera');
    } catch (caughtError) {
      const name = caughtError instanceof DOMException ? caughtError.name : '';
      const message =
        name === 'NotAllowedError'
          ? 'Camera permission was not granted. Upload a portrait instead, or allow camera access in the browser settings.'
          : name === 'NotFoundError'
            ? 'No available camera was found. Upload a portrait or use manual measurements.'
            : 'The camera could not start. Upload a portrait or use manual measurements.';
      setError(message);
      setStatus('error');
    }
  }, [closeCamera, onAnalysisChange, onPhotoChange]);

  const captureCamera = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      const canvas = captureVideoFrame(video);
      const file = await canvasToPhotoFile(canvas);
      canvas.width = 1;
      canvas.height = 1;
      closeCamera();
      await processFile(file);
    } catch (caughtError) {
      setError(describeUnknownError(caughtError, 'The camera frame could not be captured.'));
      setStatus('error');
    }
  }, [closeCamera, processFile]);

  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!cameraOpen || !video || !stream) return;
    video.srcObject = stream;
    void video.play().catch(() => {
      setError('The camera preview could not play. Upload a portrait instead.');
      setStatus('error');
      closeCamera();
    });
  }, [cameraOpen, closeCamera]);

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const item = [...(event.clipboardData?.items ?? [])].find((candidate) =>
        candidate.type.startsWith('image/'),
      );
      const file = item?.getAsFile();
      if (!file) return;
      event.preventDefault();
      void processFile(file);
    };

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [processFile]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      runIdRef.current += 1;
      stopMediaStream(streamRef.current);
    };
  }, []);

  const chooseFile = () => inputRef.current?.click();

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files[0];
    if (file) void processFile(file);
  };

  const cancelCamera = () => {
    closeCamera();
    setStatus('idle');
    setError(null);
  };

  const busy = status === 'preparing' || status === 'loading-engine' || status === 'analyzing';

  return (
    <section
      id="photo-panel"
      className="analyzer-panel"
      role="tabpanel"
      aria-labelledby="photo-tab"
      aria-busy={busy}
    >
      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        accept={PHOTO_ACCEPT_ATTRIBUTE}
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = '';
          if (file) void processFile(file);
        }}
      />

      {cameraOpen ? (
        <div className="camera-stage">
          <video ref={videoRef} className="camera-stage__video" autoPlay muted playsInline aria-label="Camera preview" />
          <div className="camera-stage__guide" aria-hidden="true">
            <ShapeGlyph shape="oval" measured />
          </div>
          <div className="camera-stage__actions">
            <button className="button button--primary" type="button" onClick={() => void captureCamera()}>
              <CameraIcon />
              Capture & analyze
            </button>
            <button className="button button--quiet" type="button" onClick={cancelCamera}>
              Cancel camera
            </button>
          </div>
        </div>
      ) : photo ? (
        <div className={`photo-stage photo-stage--${status}`}>
          <div className="photo-stage__canvas">
            <img
              src={photo.previewUrl}
              width={photo.width}
              height={photo.height}
              alt="Selected portrait with face-shape measurements"
            />
            {analysis && showOverlay ? (
              <svg
                className="face-overlay"
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                aria-label="Measured face outline, forehead, cheekbone, jaw, and length lines"
                role="img"
              >
                <path className="face-overlay__contour" d={ovalPath} vectorEffect="non-scaling-stroke" />
                {analysis.segments.map((segment) => (
                  <g key={segment.id} className={`face-overlay__segment face-overlay__segment--${segment.id}`}>
                    <line
                      x1={segment.start.x}
                      y1={segment.start.y}
                      x2={segment.end.x}
                      y2={segment.end.y}
                      vectorEffect="non-scaling-stroke"
                    />
                    <rect x={segment.start.x - 0.006} y={segment.start.y - 0.006} width="0.012" height="0.012" />
                    <rect x={segment.end.x - 0.006} y={segment.end.y - 0.006} width="0.012" height="0.012" />
                  </g>
                ))}
              </svg>
            ) : null}
            {busy ? <div className="scan-line" aria-hidden="true" /> : null}
          </div>
          <div className="photo-stage__toolbar">
            {analysis ? (
              <button className="button button--quiet button--small" type="button" onClick={() => setShowOverlay((current) => !current)}>
                {showOverlay ? <EyeOffIcon /> : <EyeIcon />}
                {showOverlay ? 'Hide measurements' : 'Show measurements'}
              </button>
            ) : null}
            <button className="button button--quiet button--small" type="button" onClick={chooseFile} disabled={busy}>
              <UploadIcon />
              Choose another
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`drop-stage${dragActive ? ' drop-stage--active' : ''}`}
          onDragEnter={(event) => {
            event.preventDefault();
            setDragActive(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            if (event.currentTarget === event.target) setDragActive(false);
          }}
          onDrop={handleDrop}
        >
          <div className="drop-stage__art" aria-hidden="true">
            <ShapeGlyph shape="oval" measured />
            <span className="drop-stage__crosshair" />
          </div>
          <div className="drop-stage__copy">
            <span className="eyebrow">One face • straight on • good light</span>
            <h2>{dragActive ? 'Release to analyze locally' : 'Add a front-facing portrait'}</h2>
            <p>Keep your full forehead, cheekbones, jaw, and chin visible. Neutral expressions are easiest to measure.</p>
          </div>
          <div className="drop-stage__actions">
            <button className="button button--primary" type="button" onClick={chooseFile}>
              <UploadIcon />
              Upload portrait
            </button>
            <button className="button button--secondary" type="button" onClick={() => void openCamera()}>
              <CameraIcon />
              Use camera
            </button>
          </div>
          <p className="drop-stage__paste">
            <PasteIcon /> Paste an image anywhere on this page. JPG, PNG, or WebP up to 12 MB.
          </p>
        </div>
      )}

      <div className="analyzer-status" role="status" aria-live="polite">
        <span className={`status-dot status-dot--${status}`} aria-hidden="true" />
        <span>{statusCopy(status)}</span>
        {delegate && analysis ? <span className="analyzer-status__detail">{delegate} processing</span> : null}
      </div>

      {error ? (
        <div className="callout callout--error" role="alert">
          <AlertIcon />
          <div>
            <strong>We could not make a reliable measurement.</strong>
            <p>{error}</p>
            <div className="callout__actions">
              <button className="text-button" type="button" onClick={chooseFile}>
                Choose another photo
              </button>
              <button className="text-button" type="button" onClick={onUseManual}>
                <RulerIcon /> Measure manually
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="privacy-inline">
        <LockIcon />
        <p>
          <strong>Your photo never leaves this tab.</strong> The analyzer files load from this same site, then the photo, landmarks, and result stay in browser memory until you reset or close the page.
        </p>
        <SparkIcon aria-hidden="true" />
      </div>
    </section>
  );
}
